import { produce } from 'immer';
import type { NodeEntry } from 'slate';
import { Editor, Node, Range, Text } from 'slate';

import { checkboxStateText } from '../../constants';
import type { UUID } from '../../types/type-helpers';
import { first } from '../../util';
import type {
  CheckboxOptionFieldElement,
  DynamicBlockElement,
  DynamicContentElement,
  ExpandedEmptyBlocks,
  FakeElement,
  ProcessTemplateField,
  TextProps,
  Trigger,
} from '../internal';
import {
  createDefaultParagraph,
  createDefaultText,
  getCheckboxInitialValue,
  isAlineEditor,
  isCheckboxElement,
  isCheckboxOptionFieldElement,
  isDynamicContentElement,
  isInputFieldElement,
  isSignatureFieldElement,
  normalizeCheckboxOptionLabelText,
  Queries,
} from '../internal';

export const dynamicBlockQueries = {
  /**
   * Determine if a DC has signatures.
   */
  dynamicElementHasSignatures(dynamicContentElement: DynamicContentElement): boolean {
    const { triggers = [] } = dynamicContentElement;

    return triggers.some(element => {
      const fakeElement: FakeElement = {
        children: element.contentJSON,
        suggestedEdits: [],
        type: 'fake',
      };
      const nodes = [...Node.nodes(fakeElement)];

      return nodes.some(([node]) => isSignatureFieldElement(node));
    });
  },
  /**
   * Get all expanded blocks.
   */
  getExpandedEmptyBlocks(editor: Editor): ExpandedEmptyBlocks {
    if (!isAlineEditor(editor)) return new Set();
    return editor.userState?.expandedEmptyBlocks ?? new Set();
  },
  /**
   * "Initializes" the contentJSON of a DynamicContentElement for saving in a trigger.contentJSON of that
   * DynamicContentElement.
   */
  getInitializedTriggerJSON(args: {
    dynamicContentNode: Pick<DynamicContentElement, 'children'>;
    fields: Record<UUID, ProcessTemplateField>;
  }): Trigger['contentJSON'] {
    const { dynamicContentNode, fields } = args;

    return produce(dynamicContentNode.children, draftChildren => {
      const fakeElement: FakeElement = {
        children: draftChildren,
        suggestedEdits: [],
        type: 'fake',
      };
      const nodes = [...Node.nodes(fakeElement)];

      for (const [node] of nodes) {
        if (isCheckboxOptionFieldElement(node)) {
          initializeCheckboxOptionField(node, fields[node.fieldId]);
        } else if (isInputFieldElement(node)) {
          // We're at an input field inside the trigger.contentJSON.
          const firstTextEntry = first(Node.texts(node));
          if (!firstTextEntry) continue;

          const [firstText] = firstTextEntry;
          if (!Text.isText(firstText)) continue;

          const initializedFieldValue = Queries.getInitializedFieldValue({
            fields,
            inputField: node,
          });

          node.children = [{ ...firstText, text: initializedFieldValue }];
        } else if (isDynamicContentElement(node)) {
          // We're at a nested DC inside the trigger.contentJSON.
          const { initialTriggerIndex, triggers = [] } = node;
          const initializedTrigger =
            typeof initialTriggerIndex === 'number' ? triggers[initialTriggerIndex] : null;
          const initializedChildren = initializedTrigger ? initializedTrigger.contentJSON : [];

          node.children = initializedChildren;
          node.triggerIndex = initialTriggerIndex;
          node.triggers = triggers.map(trigger => ({
            ...trigger,
            contentJSON: Queries.getInitializedTriggerJSON({
              dynamicContentNode: { children: trigger.contentJSON },
              fields,
            }),
          }));
        }
      }
    });
  },

  /**
   * Determines whether a DynamicBlock is "collapsed" - i.e., not rendered in the document.
   */
  getIsCollapsedDynamicBlock(
    editor: Editor,
    options: { dynamicBlockEntry: NodeEntry<DynamicBlockElement> }
  ): boolean {
    const { dynamicBlockEntry } = options;
    const [dynamicBlock, dynamicBlockPath] = dynamicBlockEntry;
    const expandedEmptyBlocks = Queries.getExpandedEmptyBlocks(editor);
    // If the empty block is expanded in state, show it expanded.
    if (expandedEmptyBlocks.has(dynamicBlock.fieldId) || dynamicBlock.triggerIndex === 'not-set')
      return false;

    // Perf: Do this upfront so we short-circuit if the dynamicBlock is non-empty.
    if (!Queries.getIsCollapsibleDynamicBlock(dynamicBlock)) return false;
    if (!editor.selection) return true;

    const dynamicBlockRange = Editor.range(editor, dynamicBlockPath);

    // Show empty DC collapsed unless the selection is entirely inside the DC.
    return !Range.surrounds(dynamicBlockRange, editor.selection);
  },

  getIsCollapsibleDynamicBlock(dynamicBlock: DynamicBlockElement): boolean {
    return Queries.nodeIsEmpty(dynamicBlock);
  },

  /**
   * Get the trigger at triggerIndex, if any.
   */
  getSelectedTrigger(
    dynamicContent: Pick<DynamicContentElement, 'triggerIndex' | 'triggers'>
  ): Trigger | null {
    const { triggerIndex, triggers = [] } = dynamicContent;
    if (typeof triggerIndex !== 'number') return null;

    return triggers[triggerIndex] ?? null;
  },

  /**
   * Get the correct children of a Trigger.
   *
   * Take into account that nodes (e.g., nested DCs)
   * in trigger.contentJSON may have empty children on purpose.
   */
  getTriggerChildren(
    newTrigger: Trigger | 'not-set',
    options: { textProps: TextProps }
  ): Trigger['contentJSON'] {
    const { textProps } = options;
    if (newTrigger === 'not-set' || !newTrigger.contentJSON.length)
      return [createDefaultParagraph({ text: '', textProps })];

    return produce(newTrigger.contentJSON, draftChildren => {
      const fakeElement: FakeElement = {
        children: draftChildren,
        suggestedEdits: [],
        type: 'fake',
      };

      for (const [node] of Node.nodes(fakeElement)) {
        if (!isDynamicContentElement(node) || node.children.length) continue;

        // Nested DC has no children.  Give it a default paragraph.
        node.children = [createDefaultParagraph({ text: '', textProps })];
      }
    });
  },
};

/**
 * Initializes one checkbox option inside trigger contentJSON: its checked state and, when the
 * option is known to the field, its label.
 *
 * Unlike the document body, the option *set* is left alone. Only the active trigger's content
 * lives in the editor, so the field's authoritative options are derived from active content
 * only — pruning against them here would delete options that exist solely in an inactive
 * trigger, and appending would inject options into content that never had them.
 */
function initializeCheckboxOptionField(
  node: CheckboxOptionFieldElement,
  field: ProcessTemplateField | undefined
): void {
  const checkboxIndex = node.children.findIndex(isCheckboxElement);
  const checkbox = node.children[checkboxIndex];
  if (checkboxIndex === -1 || !checkbox || !isCheckboxElement(checkbox)) return;

  const checkboxInitialValue = getCheckboxInitialValue(field);
  if (!checkboxInitialValue) return;

  const { answerOptions, selectedAnswerIds } = checkboxInitialValue;
  // Rename options still keyed by their AnswerOption row id onto the option's choiceRefId.
  const legacyOption = answerOptions.find(
    option => option.legacyId === node.id && option.id !== node.id
  );
  if (legacyOption) {
    node.id = legacyOption.id;
    checkbox.optionId = legacyOption.id;
  }

  if (selectedAnswerIds) {
    const firstCheckboxText = checkbox.children[0];
    checkbox.children = [
      {
        ...firstCheckboxText,
        text: checkboxStateText(selectedAnswerIds.includes(node.id)),
      },
    ];
  }

  const answerOption = answerOptions.find(option => option.id === node.id);
  if (!answerOption) return;

  const childrenAfterCheckbox = node.children.slice(checkboxIndex + 1);
  const labelTextChildren = childrenAfterCheckbox.filter(child => Text.isText(child));
  const labelText = normalizeCheckboxOptionLabelText(answerOption.optionText);
  if (labelTextChildren.map(textNode => textNode.text).join('') === labelText) return;

  /**
   * Replace only the label's text nodes. A checkbox option can also hold tabs, which the
   * editor-side label update preserves, so keep every non-text child after the new label.
   */
  const preservedChildren = childrenAfterCheckbox.filter(child => !Text.isText(child));
  const newLabelChildren = labelText ? [createDefaultText(labelTextChildren[0], labelText)] : [];

  node.children.splice(
    checkboxIndex + 1,
    childrenAfterCheckbox.length,
    ...newLabelChildren,
    ...preservedChildren
  );
}
